import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';

import ReactSmartGallery from '../../vendor/react-smart-gallery';
import { ReactComponent as ArrowLeft } from '../icons/icon-arrow-left-circle.svg';
import { ReactComponent as ArrowRight } from '../icons/icon-arrow-right-circle.svg';
import { ReactComponent as IconClose } from '../icons/icon-close-circle.svg';
import { isClickAwayFromElement } from '../utils/dom';
import { isMobile } from '../utils/web';

const CustomGallery = ({ photos }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const hasMultiPhotos = useMemo(() => photos.length > 1, [photos]);

  const handleImageClick = (_event: MouseEvent, _content: any, index: number) => {
    setCurrentImageIndex(index);
    dialogRef.current?.showModal();
  };

  const closeLightbox = () => {
    dialogRef.current?.close();
  };

  const nextImage = () => {
    setCurrentImageIndex((currentImageIndex! + 1) % photos.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((currentImageIndex + photos.length - 1) % photos.length);
  };

  useEffect(() => {
    const currentDialog = dialogRef.current;

    if (currentDialog?.open) {
      const handleDialogCloseOnClickAway = (e: MouseEvent) =>
        currentDialog.open && isClickAwayFromElement(e, currentDialog) && currentDialog.close();

      currentDialog.addEventListener('click', e => handleDialogCloseOnClickAway(e));

      return currentDialog.removeEventListener('click', e => handleDialogCloseOnClickAway(e));
    }
  }, [dialogRef]);

  return (
    <div>
      <ReactSmartGallery
        images={photos}
        width="100%"
        height={isMobile() ? 250 : 400}
        onImageSelect={handleImageClick}
      />
      <dialog
        ref={dialogRef}
        className="lightbox bg-transparent backdrop:bg-black/80 backdrop:backdrop-blur-sm"
      >
        <button
          className="close fixed  top-5 right-5 text-white opacity-60 transition-all hover:opacity-100"
          onClick={closeLightbox}
        >
          <IconClose className="h-8 w-8" />
        </button>
        <div className="image-container mx-auto relative flex items-center overflow-hidden max-w-[95vw] h-[80vh] md:max-w-[80vw] md:h-[80vh]">
          {hasMultiPhotos && (
            <div className="prev absolute left-0 bg-none h-full flex items-center justify-center">
              <button
                className="pl-2 border-none text-white opacity-60 transition-all hover:opacity-100 text-xl cursor-pointer"
                onClick={prevImage}
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
            </div>
          )}
          {hasMultiPhotos && (
            <div className="next absolute right-0 bg-none h-full flex items-center justify-center">
              <button
                className="pr-2 border-none text-white opacity-60 transition-all hover:opacity-100 text-xl cursor-pointer"
                onClick={nextImage}
              >
                <ArrowRight className="h-6 w-6" />
              </button>
            </div>
          )}
          <Image
            src={photos[currentImageIndex!]}
            width={800}
            height={400}
            alt={`image ${currentImageIndex}`}
            className="max-w-full max-h-full object-center object-contain"
          />
        </div>
      </dialog>
    </div>
  );
};

export default CustomGallery;
